/**
 * Manual Campaign Booking Resolver (Client-Side)
 * 
 * This helper function resolves booking configuration for manual campaigns
 * based on media format capacity settings.
 * 
 * CRITICAL: This resolver MUST NOT be used by pack logic.
 * It is exclusively for manual campaign creation flows.
 * 
 * @module resolveManualCampaignBooking
 */

export interface BookingConfiguration {
  capacity: "unlimited" | "limited";
  bookingType: "percentage" | "absolute" | null;
  bands: number[];
  absoluteLimitPerStore: number | null;
}

export interface MediaFormatBookingInput {
  capacityType?: string | null;
  bookingType?: string | null;
  bookingMeta?: {
    bands?: number[];
    absoluteLimitPerStore?: number;
  } | null;
}

/**
 * Default percentage bands for legacy PopAd formats
 * Used when bookingMeta is not set
 */
const DEFAULT_PERCENTAGE_BANDS = [25, 50, 75, 100];

/**
 * Resolves booking configuration for a media format in manual campaign context.
 * 
 * This function provides:
 * 1. Legacy fallback for PopAds with null bookingType (assumes percentage with standard bands)
 * 2. Proper resolution of new booking fields
 * 
 * @param mediaFormat - The media format with optional booking configuration
 * @returns BookingConfiguration - Resolved booking settings
 * 
 * @example
 * // Legacy PopAd (capacityType = 'limited' but no bookingType)
 * const config = resolveManualCampaignBooking({ capacityType: 'limited' });
 * // Returns: { capacity: "limited", bookingType: "percentage", bands: [25, 50, 75, 100], absoluteLimitPerStore: null }
 * 
 * @example
 * // New format with custom bands
 * const config = resolveManualCampaignBooking({
 *   capacityType: "limited",
 *   bookingType: "percentage",
 *   bookingMeta: { bands: [10, 25, 50, 100] }
 * });
 * // Returns: { capacity: "limited", bookingType: "percentage", bands: [10, 25, 50, 100], absoluteLimitPerStore: null }
 */
export function resolveManualCampaignBooking(mediaFormat: MediaFormatBookingInput): BookingConfiguration {
  // Handle unlimited capacity (default case)
  if (!mediaFormat.capacityType || mediaFormat.capacityType === "unlimited") {
    return {
      capacity: "unlimited",
      bookingType: null,
      bands: [],
      absoluteLimitPerStore: null,
    };
  }

  // Handle limited capacity with new booking fields
  if (mediaFormat.capacityType === "limited") {
    // If booking fields are properly set, use them
    if (mediaFormat.bookingType === "percentage") {
      return {
        capacity: "limited",
        bookingType: "percentage",
        bands: mediaFormat.bookingMeta?.bands || DEFAULT_PERCENTAGE_BANDS,
        absoluteLimitPerStore: null,
      };
    }

    if (mediaFormat.bookingType === "absolute") {
      return {
        capacity: "limited",
        bookingType: "absolute",
        bands: [],
        absoluteLimitPerStore: mediaFormat.bookingMeta?.absoluteLimitPerStore || 1,
      };
    }

    // Legacy fallback for PopAds without new booking fields
    // This handles existing PopAd formats that were created before the booking fields were added
    return {
      capacity: "limited",
      bookingType: "percentage",
      bands: DEFAULT_PERCENTAGE_BANDS,
      absoluteLimitPerStore: null,
    };
  }

  // Default fallback for unknown capacity types
  return {
    capacity: "unlimited",
    bookingType: null,
    bands: [],
    absoluteLimitPerStore: null,
  };
}

/**
 * Type guard to check if a media format has limited capacity
 */
export function isLimitedCapacityFormat(mediaFormat: MediaFormatBookingInput): boolean {
  return mediaFormat.capacityType === "limited";
}

/**
 * Type guard to check if a media format uses percentage-based booking
 */
export function isPercentageBooking(config: BookingConfiguration): boolean {
  return config.capacity === "limited" && config.bookingType === "percentage";
}

/**
 * Type guard to check if a media format uses absolute limit booking
 */
export function isAbsoluteBooking(config: BookingConfiguration): boolean {
  return config.capacity === "limited" && config.bookingType === "absolute";
}

/**
 * Get available booking bands for a format
 * Returns empty array for unlimited or absolute formats
 */
export function getAvailableBands(mediaFormat: MediaFormatBookingInput): number[] {
  const config = resolveManualCampaignBooking(mediaFormat);
  return config.bands;
}

export default resolveManualCampaignBooking;

